/**
 * 이상한 방 - 돌아가는 릴의 산술 (순수. DOM도 시계도 난수도 없다)
 *
 * 근거 문서: 로드맵 확정안 3.3절(연출 시퀀스) · 4절 9번(정직한 바늘) · 0절 판정표 10번 · 6절("근접 실패" 기각)
 *           `game/combo.ts` 머리말(규칙을 시그니처로 증명한다)
 *
 * ## 왜 릴인가
 *
 * 전에는 확률 띠 위를 **바늘이 한 번 스윽 지나가고** 900ms 만에 멎었다. 폭이 진짜 확률이라 정직하기는 했지만
 * 한 번 지나가는 것에는 "돌아간다"가 없었고, 사람은 "누르자마자 뾱 뽑힌다"고 말했다. 그래서 같은 띠를
 * **여러 번 이어 붙인 긴 트랙**으로 만들고, 그 트랙이 빠르게 흐르다 감속해 `gradeRoll` 그 자리에 멎게 한다.
 * SSR 1%는 여전히 실선 한 줄이고, 그 실선이 눈앞을 대여섯 번 스쳐 가는 것 자체가 긴장이다.
 *
 * ## 정직함을 시그니처로 못박는다 (이 파일의 존재 이유)
 *
 * `combo.ts`가 함수 시그니처에 `ms`를 하나도 두지 않는 것으로 "콤보는 시간으로 끊기지 않는다"를 증명하듯,
 * 여기서는 **위치 함수가 등급을 입력으로 받지 않는 것**으로 규칙을 증명한다:
 *
 *  1. `reelPosition(progress, landing, turns)` - 인자는 셋뿐이다. "지금 어느 등급 위를 지나는가"가 없으니
 *     SSR 근처에서 인위적으로 느려질 방법이 **없다**. 그것이 로드맵 0절 10번·6절이 기각한 근접 실패 속임수다
 *  2. **착지점은 `gradeRoll` 그 값이다.** `reelPosition(1, landing, turns)`의 소수부가 곧 `landing`이고,
 *     경계 옆으로 밀거나 보정하는 자리가 없다(`envelopeGauge.ts` 머리말의 "값을 만들지 않는다" 그대로)
 *  3. **바퀴 수와 총 길이가 등급마다 같다.** `reelTurnsFor`는 `seq`만 본다. SSR일 때 더 오래 돌면 그 순간
 *     스포일러이고, 그건 결과를 예고하는 것이라 이 화면이 하지 않기로 한 것이다
 *  4. **감속은 단조다.** 곡선이 `cubic-bezier` 하나라 되감기도 튕김도 표현할 수 없고, 속도가 다시 오르지
 *     않는다는 것을 `envelopeReel.test.ts`가 수치로 확인한다
 *
 * ## 출발 자리는 상수다
 *
 * `from`은 언제나 `REEL.pad`다. "끝에서 바퀴 수만큼 뒤로" 잡으면 사본이 전부 같은 트랙에서 **출발 위상의
 * 소수부가 곧 착지값**이 되어 0ms 프레임이 등급을 흘린다. 출발을 고정하면 흘릴 것이 없고, 대신 이동 거리가
 * 등급마다 한 바퀴 안쪽에서 달라진다 - 그건 이미 바늘이 하는 정직한 말과 같은 값이다.
 *
 * ## 똑딱은 등급을 모른다
 *
 * 세그먼트 경계를 지날 때마다 소리가 나되, 그 시각은 실제 착지값이 아니라 **명목 이동 거리**
 * (`turns + REEL.nominalLanding`)에서 뽑는다. 실제 값으로 뽑으면 소리의 개수와 마지막 똑딱의 시각이
 * 착지값을 그대로 말해 버린다 - 눈으로 보이는 릴은 정직해도 되지만, 소리 채널로 같은 값을 한 번 더
 * 흘릴 이유는 없다.
 *
 * **그 대가는 소리와 그림의 위상이 어긋나는 것이다.** 롤이 0.5에서 멀수록 똑딱은 실제 경계 통과와
 * 최대 반 바퀴까지 벌어지고, SSR(롤 0.99) 회차에서는 마지막 똑딱이 N 띠 한가운데에서 울린다.
 * 그래도 이 쪽을 고른 이유는 **둘 다 가질 수 없기 때문이다** - 시각과 맞추려면 시각이 착지값을 알아야 하고,
 * 아는 순간 개수와 마지막 시각이 등급을 말한다. 그래서 똑딱은 "경계를 지났다"가 아니라 **감속의 박자**로
 * 읽히게 두었다. 사본이 전부 같아 반복 무늬로는 어긋남이 보이지 않고, 남는 것은 리듬뿐이다.
 *
 * ## 결(눈금)은 정보가 아니라 속도계다
 *
 * 사본마다 같은 간격의 눈금을 새긴다(`grainPerCopy`, 그리는 것은 `envelopeReel.css`). **브라우저에서
 * 실제로 보고 나서 생긴 값이다** - N이 띠의 60%라 평평한 회색 한 장 위를 지나는 동안에는 릴이 돌고
 * 있어도 눈에는 멎어 있었고, 그 구간이 하필 릴의 마지막 사본(당시 곡선에서 0.9초)이었다. 눈금은 네
 * 세그먼트 위에 **똑같이** 덮이고 폭을 하나도 건드리지 않으므로 확률표에 대해 아무 말도 하지 않는다 -
 * 속도만 읽히게 하는 물건이다.
 *
 * ## 이 파일에는 DOM이 없다
 *
 * 트랙을 짓고 클래스를 얹는 것은 `envelopeGauge.ts`, 언제 무엇이 일어나는가는 `envelopePhases.ts`다.
 * 여기 있는 것은 표와 순수 함수뿐이라 `setTimeout`도 `Math.random`도 없다(`envelopePhases.test.ts`가 검사한다).
 */

import { clamp01 } from '../core/math'
import { gradeBands } from '../game/economy/envelope'
import { ENVELOPE_TABLE, type EnvelopeTable } from '../game/economy/envelopeTable'

/* --- 표 ------------------------------------------------------------------------ */

/**
 * 릴의 모양과 결. **길이(ms)는 여기 없다** - 릴이 도는 시간은 개봉 전 시각표(`ENVELOPE_TIMELINE`)의
 * `riseMs ~ sweepEndMs` 구간 그 자체이고, 그 값을 여기에 한 벌 더 적으면 표가 둘이 된다.
 */
export const REEL = {
  /**
   * 창에 한 번에 보이는 사본 수. 둘이라 띠가 두 벌 보이고, 한 벌이 흘러 나가는 동안 다음 벌이 들어온다 -
   * 하나면 "판이 바뀐다"로 읽히고 셋 이상이면 SSR 실선이 너무 가늘어 눈에 안 걸린다.
   */
  perWindow: 2,
  /** 마커 왼쪽에 남겨 두는 여유 사본. 출발 순간 창의 왼쪽 절반이 비지 않게 하는 최소값이다. */
  pad: 1,
  /** 감속 구간이 도는 최소 바퀴 수. */
  minTurns: 5,
  /** `seq`가 고르는 바퀴 수의 폭. 5~7바퀴가 되고, **등급은 이 선택에 참여하지 않는다**. */
  turnSpan: 3,
  /**
   * 똑딱 일정을 뽑을 때 쓰는 명목 착지값. 실제 `gradeRoll` 대신 이 상수를 쓰는 것이 "똑딱은 등급을 모른다"의
   * 전부다(머리말). 0.5는 [0,1)의 한가운데라 어느 등급으로도 기울지 않는다.
   */
  nominalLanding: 0.5,
  /**
   * 똑딱 사이의 최소 간격(ms). 처음 1초는 초당 마흔 번꼴로 경계를 지나므로 그대로 울리면 소리가 아니라
   * 잡음이 된다. 촘촘한 경계를 건너뛰면 남는 것이 저절로 "빠른 딱딱딱"이고, 감속하면서 경계 전부가 살아난다.
   */
  minTickGapMs: 45,
  /**
   * 똑딱의 상한. 바퀴 수가 늘어도 소리 예약이 무한정 자라지 않게 하는 안전선이다.
   *
   * **실측은 12~15개**(5~7바퀴)라 지금 이 값에 닿는 입력은 없다. 40에서 20으로 내린 이유가 그것이다 -
   * 닿지 않는 상한은 안전선이 아니라 통과만 하는 단언이고, 실제 개수 근처에 두어야 곡선이나 표를 고쳐
   * 똑딱이 배로 늘어난 날 여기서 걸린다.
   */
  maxTicks: 20,
  /**
   * 사본 하나에 새기는 결의 수(CSS가 `--ev-reel-grain`으로 받는다. 값은 `100 / grainPerCopy` 퍼센트).
   *
   * **브라우저에서 보고 나서 생긴 값이다.** N이 띠의 60%라 사본의 절반 이상이 아무 무늬 없는 회색
   * 한 장이고, 마지막 한 사본을 지나는 동안(당시 곡선에서 0.9초) 화면에서 **움직이는 것이 하나도 없었다** -
   * 실제로는 돌고 있는데 눈에는 이미 멎은 릴이었다. 눈금을 새기면 평평한 띠 위에서도 속도가 읽힌다.
   *
   * 사본 폭의 비율이라 화면 폭이 달라져도 사본마다 정확히 이만큼이고(이음매에서 위상이 안 튄다),
   * 네 등급의 세그먼트 위에 **똑같이** 덮이므로 등급을 흘리지 않는다 - 확률 띠의 폭을 건드리지도 않는다.
   */
  grainPerCopy: 25,
  /**
   * 감속 곡선. CSS `cubic-bezier`로 그대로 나가고, 똑딱 시각도 같은 식에서 뽑는다 - 곡선이 두 벌이면
   * 소리와 그림이 갈린다. 시작 기울기는 `y1/x1 = 3.5`(평균 속도의 세 배 반)이고 끝 기울기는 0이다.
   *
   * **끝을 덜 눕혔다**(전 `0.1, 0.4, 0.4, 1`). 앞이 너무 쏠려 있어서 마지막 경계를 릴 시간의 63~65%
   * 지점에서 이미 지나갔고, 남은 0.9초는 넓은 N 띠 안을 기어가는 죽은 구간이었다 - 똑딱도 그때
   * 멎는다(경계가 없으니 울릴 것이 없다).
   *
   * 지금은 **바퀴 수에 따라 71.5%(5바퀴) · 74.0%(6) · 75.9%(7)** 지점이고 죽은 꼬리는 0.74 / 0.68 / 0.63초다.
   * 가장 나쁜 회차는 `seq % 3 === 0`(5바퀴, 꼬리가 릴의 28.5%)이라 회귀 잠금(`envelopeReel.test.ts`의 30%)까지
   * 여유가 1.5%p뿐이다 - 곡선을 조금만 앞으로 되돌려도 거기서 걸린다.
   * 출발이 여전히 평균의 3.5배라 흐릿하게 시작하는 것은 그대로다.
   */
  ease: { x1: 0.1, y1: 0.35, x2: 0.6, y2: 1 },
} as const

/** CSS에 그대로 나가는 곡선 문자열. 값의 원본은 위 표 하나다. */
export const REEL_EASE_CSS = `cubic-bezier(${REEL.ease.x1}, ${REEL.ease.y1}, ${REEL.ease.x2}, ${REEL.ease.y2})`

/** CSS에 그대로 나가는 결의 간격(사본 폭 대비 %). 값의 원본도 위 표 하나다. */
export const REEL_GRAIN_CSS = `${(100 / REEL.grainPerCopy).toFixed(3)}%`

/* --- 타입 ---------------------------------------------------------------------- */

/** 이번 회차의 릴. 전부 `gradeRoll`과 `seq`에서 파생한다 - 난수는 하나도 만들지 않는다. */
export type ReelRun = Readonly<{
  /** 트랙에 이어 붙일 사본 수. 등급과 무관하다(노드 수가 등급을 흘리면 안 된다). */
  copies: number
  /** 감속 구간이 도는 바퀴 수. 등급과 무관하다. */
  turns: number
  /** 감속이 시작되는 자리(사본 단위). 상수라 0ms 프레임이 네 등급 모두 같다. */
  from: number
  /** 착지 자리. 소수부가 `gradeRoll` 그 값이다. */
  to: number
}>

/* --- 곡선 ---------------------------------------------------------------------- */

/** 3차 베지에의 한 축 계수. `cubic-bezier(x1,y1,x2,y2)`의 표준 전개다(P0 = 0, P3 = 1). */
function axis(p1: number, p2: number): { a: number; b: number; c: number } {
  return { a: 3 * p1 - 3 * p2 + 1, b: 3 * p2 - 6 * p1, c: 3 * p1 }
}

function axisAt(u: number, p1: number, p2: number): number {
  const { a, b, c } = axis(p1, p2)
  return ((a * u + b) * u + c) * u
}

/** 이분법 40회. 두 축 모두 단조라 해가 하나뿐이고, 뉴턴법을 쓸 만큼 정밀도가 필요한 자리가 아니다. */
function axisSolve(target: number, p1: number, p2: number): number {
  let lo = 0
  let hi = 1
  for (let i = 0; i < 40; i += 1) {
    const mid = (lo + hi) / 2
    if (axisAt(mid, p1, p2) < target) lo = mid
    else hi = mid
  }
  return (lo + hi) / 2
}

/** 진행률(0~1) → 이동 비율(0~1). CSS의 `cubic-bezier`가 하는 것과 같은 계산이다. */
export function reelEase(progress: number): number {
  const { x1, y1, x2, y2 } = REEL.ease
  return clamp01(axisAt(axisSolve(clamp01(progress), x1, x2), y1, y2))
}

/** 이동 비율(0~1) → 진행률(0~1). `reelEase`의 역함수다 - 똑딱이 "언제" 울리는가가 여기서 나온다. */
export function reelEaseAt(ratio: number): number {
  const { x1, y1, x2, y2 } = REEL.ease
  return clamp01(axisAt(axisSolve(clamp01(ratio), y1, y2), x1, x2))
}

/** 출발 순간의 속도 배수(평균 속도 대비). 리드 스핀의 빠르기를 여기에 맞춰 이음매에서 속도가 튀지 않게 한다. */
function startSlope(): number {
  return REEL.ease.y1 / REEL.ease.x1
}

/* --- 위치 ---------------------------------------------------------------------- */

/**
 * 진행률 → 트랙 위치(사본 단위).
 *
 * **인자가 셋뿐인 것이 이 파일의 규칙이다**(머리말). 등급도, "지금 어느 띠 위인가"도 받지 않으므로
 * SSR 근처에서 느려지거나 되감기는 연출을 이 함수로는 쓸 수 없다. 단조 증가이고, 속도(도함수)는
 * 단조 감소한다 - 곡선이 `REEL.ease` 하나이기 때문이다.
 */
export function reelPosition(progress: number, landing: number, turns: number): number {
  const travel = Math.max(0, turns) + clamp01(landing)
  return REEL.pad + travel * reelEase(progress)
}

/** `seq` → 바퀴 수. **등급을 보지 않는다** - SSR이 더 오래 돌면 도는 것 자체가 예고가 된다. */
export function reelTurnsFor(seq: number): number {
  const safe = Number.isFinite(seq) ? Math.floor(Math.abs(seq)) : 0
  return REEL.minTurns + (safe % REEL.turnSpan)
}

/** 이번 회차의 릴. 사본 수는 `turns`에서만 나오므로 네 등급이 같은 트랙을 본다. */
export function reelRunFor(gradeRoll: number, seq: number): ReelRun {
  const turns = reelTurnsFor(seq)
  return {
    turns,
    // 착지 자리(`pad + turns + landing`) 오른쪽에 사본 하나가 더 있어야 창의 오른쪽 절반이 비지 않는다.
    copies: REEL.pad + turns + 2,
    from: REEL.pad,
    to: reelPosition(1, gradeRoll, turns),
  }
}

/**
 * 트랙 위치 → `translateX`에 곱할 배수(트랙 자기 폭 기준).
 *
 * 창 한가운데의 마커 아래에 위치 `pos`가 오게 하는 값이다. 한 사본이 창의 `1/perWindow`이므로
 * 트랙 폭은 `copies / perWindow` 창이고, 그 안에서의 비율이 아래 한 줄이다.
 */
export function reelShift(pos: number, copies: number): number {
  if (copies <= 0) return 0
  return (REEL.perWindow / 2 - pos) / copies
}

/**
 * 열 장에서 앞의 아홉이 흐르는 동안 릴이 도는 한 바퀴의 길이(ms). 0이면 리드 스핀이 없다(한 장짜리).
 *
 * **`leadMs`를 정확히 나눈다** - 그러나 그것은 **명목 길이에 대해서만** 참이다. 실제 리드는 카드 아홉 장을
 * 넘기는 `setTimeout` 아홉 개의 실시간이고 하나마다 몇 ms씩 늦으므로, 감속이 시작되는 순간 CSS 애니메이션
 * (벽시계로 흐른다)의 위상은 0이 아니다. 그 잔여를 실제로 걷어 내는 것은 `reelCarry`이고 이 값은 그
 * 어긋남을 **작게 만드는** 몫만 한다. 전에는 여기서 "정수 바퀴라 위상 0"이라고 못박았는데, 그 말대로 믿고
 * 이음매를 하드컷하면 트랙이 잔여 위상만큼(측정 0.25 사본 ≈ 44px) 가로로 튄다.
 *
 * 빠르기는 감속이 시작될 때의 속도(명목)와 같게 잡는다. 이어지는 순간 속도가 오르면 "계속 돌고 있었다"가 아니라
 * "이제부터 돈다"로 읽히고, 그건 단조 감속의 약속과도 어긋난다.
 */
export function reelSpinPeriodMs(leadMs: number, spinMs: number, seq: number): number {
  if (!(leadMs > 0) || !(spinMs > 0)) return 0
  const nominalTravel = reelTurnsFor(seq) + REEL.nominalLanding
  const copiesPerMs = (nominalTravel * startSlope()) / spinMs
  const laps = Math.max(1, Math.round(copiesPerMs * leadMs))
  return leadMs / laps
}

/**
 * 리드가 끝나는 순간 제자리 회전이 남겨 둔 **잔여 위상**(사본 단위, 0 이상 1 미만).
 *
 * 리드는 사본 하나를 옮기는 애니메이션의 반복이라, 도중에 그 애니메이션을 걷으면 트랙은 걷힌 위상만큼
 * 왼쪽으로 튄다. 명목상으로는 반복이 정수 번 끝나 위상이 0이지만(`reelSpinPeriodMs`), 실제 리드 길이는
 * 타이머 아홉 개의 실시간이라 몇 ms씩 길다. 그 몇 ms가 사본의 몇 %가 되고, 폰에서 사본 하나가 177px이라
 * 0.25 사본이면 44px짜리 가로 점프다 - 눈금(`grainPerCopy`)이 생긴 뒤로는 그 점프가 무늬로도 보인다.
 *
 * 그래서 감속을 위상 0이 아니라 **지금 보이는 자리에서** 이어 붙인다. 되감기가 아니라 이어 붙이기라
 * 단조 증가는 그대로이고, 착지 자리(`run.to`)는 절대값이라 하나도 움직이지 않는다.
 */
export function reelCarry(elapsedMs: number, periodMs: number): number {
  if (!(periodMs > 0) || !(elapsedMs > 0)) return 0
  return (elapsedMs % periodMs) / periodMs
}

/* --- 똑딱 --------------------------------------------------------------------- */

/**
 * 세그먼트 경계를 지나는 시각들(ms, 릴이 출발한 순간 기준). 감속하므로 간격이 저절로 벌어진다.
 *
 * 경계는 `gradeBands(table)`의 누적값 그대로다 - 여기서 폭을 지어내면 소리가 확률표와 다른 말을 한다.
 * 시각은 실제 착지값이 아니라 명목 이동 거리에서 뽑는다(머리말 "똑딱은 등급을 모른다").
 */
export function reelTickTimes(spinMs: number, seq: number, table: EnvelopeTable = ENVELOPE_TABLE): readonly number[] {
  if (!(spinMs > 0)) return []
  const turns = reelTurnsFor(seq)
  const nominalTravel = turns + REEL.nominalLanding
  const edges = gradeBands(table).map((band) => band.to)
  const times: number[] = []
  let last = Number.NEGATIVE_INFINITY

  for (let lap = 0; lap < turns + 2; lap += 1) {
    for (const edge of edges) {
      const distance = lap + edge
      if (distance > nominalTravel) return times
      const at = spinMs * reelEaseAt(distance / nominalTravel)
      if (at - last < REEL.minTickGapMs) continue
      last = at
      times.push(Math.round(at))
      if (times.length >= REEL.maxTicks) return times
    }
  }
  return times
}
